import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, MessageSquare, Send, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getBlogPost, getBlogComments, createBlogComment, deleteBlogComment } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BlogPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    getBlogPost(id)
      .then((res) => setPost(res.data))
      .catch((err) => {
        console.error(err);
        toast.error('Failed to load blog post');
        navigate('/blog');
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => {
    if (id) {
      fetchComments();
    }
  }, [id]);

  const fetchComments = async () => {
    setCommentsLoading(true);
    try {
      const res = await getBlogComments(id);
      setComments(res.data || []);
    } catch (err) {
      console.error('Failed to load comments:', err);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }
    if (newComment.trim().length < 3) {
      toast.error('Comment must be at least 3 characters');
      return;
    }

    setSubmitting(true);
    try {
      await createBlogComment(id, { content: newComment });
      toast.success('Comment added successfully');
      setNewComment('');
      fetchComments();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      await deleteBlogComment(id, commentId);
      toast.success('Comment deleted');
      fetchComments();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to delete comment');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCommentDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return formatDate(dateStr);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B1818] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading post...</p>
        </div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen py-12 bg-[#F9F9F7]" data-testid="blog-post-page">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button
          onClick={() => navigate('/blog')}
          variant="ghost"
          className="mb-8"
          data-testid="back-to-blog-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Blog
        </Button>

        {/* Post Header */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-200 mb-8">
          {post.image_url && (
            <div className="aspect-[21/9]">
              <img
                src={post.image_url}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8 lg:p-12">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6" data-testid="post-title">
              {post.title}
            </h1>

            <div className="flex items-center space-x-6 text-slate-600 mb-8">
              <div className="flex items-center space-x-2">
                <User className="h-5 w-5" />
                <span className="font-medium">{post.author}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="h-5 w-5" />
                <span>{formatDate(post.created_at)}</span>
              </div>
            </div>

            {/* Post Content */}
            <div className="prose prose-lg max-w-none" data-testid="post-content">
              <div 
                className="text-slate-700 leading-relaxed blog-content"
                dangerouslySetInnerHTML={{ __html: post.content.replace(/&nbsp;/g, ' ') }}
              />
              <style>{`
                .blog-content,
                .blog-content * {
                  font-size: 16px !important;
                  line-height: 1.8 !important;
                  text-align: left !important;
                  white-space: normal !important;
                  word-break: normal !important;
                  overflow-wrap: normal !important;
                  hyphens: none !important;
                  -webkit-hyphens: none !important;
                }
                .blog-content p {
                  margin-bottom: 1em;
                  display: block !important;
                }
                .blog-content img {
                  max-width: 100%;
                  height: auto;
                  border-radius: 8px;
                  margin: 16px 0;
                }
                .blog-content h1 {
                  font-size: 2em !important;
                  font-weight: bold;
                  margin: 0.67em 0;
                }
                .blog-content h2 {
                  font-size: 1.5em !important;
                  font-weight: bold;
                  margin: 0.83em 0;
                }
                .blog-content h3 {
                  font-size: 1.17em !important;
                  font-weight: bold;
                  margin: 1em 0;
                }
                .blog-content ul {
                  list-style-type: disc;
                  padding-left: 1.5em;
                  margin: 1em 0;
                }
                .blog-content ol {
                  list-style-type: decimal;
                  padding-left: 1.5em;
                  margin: 1em 0;
                }
                .blog-content li {
                  margin: 0.5em 0;
                }
                .blog-content a {
                  color: #8B1818;
                  text-decoration: underline;
                }
                .blog-content .ql-size-small {
                  font-size: 0.75em !important;
                }
                .blog-content .ql-size-large {
                  font-size: 1.5em !important;
                }
                .blog-content .ql-size-huge {
                  font-size: 2.5em !important;
                }
                .blog-content .ql-align-center {
                  text-align: center !important;
                }
                .blog-content .ql-align-right {
                  text-align: right !important;
                }
                .blog-content .ql-align-justify {
                  text-align: justify !important;
                }
                .blog-content .ql-indent-1 {
                  padding-left: 3em;
                }
                .blog-content .ql-indent-2 {
                  padding-left: 6em;
                }
                .blog-content strong, .blog-content b {
                  font-weight: bold;
                }
                .blog-content em, .blog-content i {
                  font-style: italic;
                }
                .blog-content u {
                  text-decoration: underline;
                }
                .blog-content s {
                  text-decoration: line-through;
                }
              `}</style>
            </div>
          </div>
        </div>

        {/* Back to Blog CTA */}
        <div className="text-center mb-8">
          <Button onClick={() => navigate('/blog')} className="btn-secondary">
            Read More Articles
          </Button>
        </div>

        {/* Comments Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 lg:p-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-slate-900 flex items-center">
              <MessageSquare className="h-6 w-6 mr-2 text-[#8B1818]" />
              Comments ({comments.length})
            </h2>
          </div>

          {/* Comment Form */}
          {user ? (
            <form onSubmit={handleSubmitComment} className="mb-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#8B1818] text-white flex items-center justify-center font-medium flex-shrink-0">
                  {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </div>
                <div className="flex-1">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Share your thoughts on this article..."
                    className="w-full p-4 border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#8B1818] focus:border-transparent"
                    rows={3}
                    maxLength={2000}
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-slate-400">{newComment.length}/2000 characters</span>
                    <Button 
                      type="submit" 
                      disabled={submitting || !newComment.trim()}
                      className="bg-[#8B1818] text-white hover:bg-[#701313]"
                    >
                      {submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Posting...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Post Comment
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          ) : (
            <div className="text-center py-8 bg-slate-50 rounded-lg mb-8">
              <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Login to Comment</h3>
              <p className="text-slate-600 mb-6">
                Join the conversation by logging in or creating an account.
              </p>
              <div className="flex gap-4 justify-center">
                <Link to="/login">
                  <Button className="bg-[#8B1818] text-white hover:bg-[#701313]">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button variant="outline">
                    Create Account
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Comments List */}
          {commentsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8B1818] mx-auto mb-2"></div>
              <p className="text-slate-500">Loading comments...</p>
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="h-12 w-12 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-500">No comments yet. Be the first to share your thoughts!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {comments.map((comment) => (
                <div key={comment.id} className="flex gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-slate-300 text-slate-700 flex items-center justify-center font-medium flex-shrink-0">
                    {comment.user_name ? comment.user_name.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-slate-900">{comment.user_name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">{formatCommentDate(comment.created_at)}</span>
                        {user && (user.id === comment.user_id || user.is_admin) && (
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            className="text-slate-400 hover:text-red-500 transition-colors"
                            title="Delete comment"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-slate-700 whitespace-pre-wrap">{comment.content}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Guidelines Notice */}
          <div className="mt-8 bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
            <p className="text-sm text-amber-900">
              <strong>Comment Guidelines:</strong> Please keep comments respectful and on-topic. Off-topic or inappropriate comments may be removed. For in-depth discussions, visit our <Link to="/forum" className="text-[#8B1818] hover:underline">Forum</Link>.
            </p>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPost;
